import { useEffect, useState } from 'react'
import type { AriaRole } from 'react'
import { KnowWhyAPIReponseType, PowerPerformanceForms, UserInvestmentType } from '../../types/mutual-fund-detail.types'
import RenderHTML from '../ui-components/RenderHTML'
import { useBuildKnowWhyBottomSheetContent } from './useBuildKnowWhyBottomSheetContent'
import { KnowWhyInvestment } from './KnowWhyInvestment'
import { EliteBanner } from './EliteBanner'
import { EventNames } from '../../types/misc.types'
import { trackEvent } from '../../utils/appBridge'

const stripHtml = (html: string) =>
  html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim()

export const KnowWhyBottomSheetContent = ({
  knowWhyData,
  ScreenName
}: {
  knowWhyData: KnowWhyAPIReponseType
  ScreenName: string
}) => {
  const [powerForm, setPowerForm] = useState<`${PowerPerformanceForms}`>(PowerPerformanceForms.PERFORMANCE_LOCKED)
  const [userInvestmentType, setUserInvsetmentType] = useState<`${UserInvestmentType}`>(UserInvestmentType.NONE)

  useEffect(() => {
    setPowerForm(knowWhyData?.powerPerformanceData?.powerRating)
    if (knowWhyData?.sipAmount?.amount > 0) {
      setUserInvsetmentType(UserInvestmentType.SIP)
    } else if (knowWhyData?.investedAmount?.amount > 0) {
      setUserInvsetmentType(UserInvestmentType.LUMPSUM)
    } else {
      setUserInvsetmentType(UserInvestmentType.NONE)
    }
  }, [knowWhyData])

  const { reasonsBanner, recommendationsBanner, nextStepsBanner, recommendationPoints, scoreSections } =
    useBuildKnowWhyBottomSheetContent({ powerForm, userInvestmentType, knowWhyData, ScreenName })

  return (
    <div className='flex flex-col w-full bg-[#F0F4F7]'>
      <div className='w-full bg-white'>{reasonsBanner}</div>
      {/* Header Investment Component */}
      <div className='text-[#6A6E6E] text-[14px] font-[400] leading-[150%] h-max bg-white pt-1 w-full px-5'>
        <KnowWhyInvestment
          logoSrc={knowWhyData?.logo ?? ''}
          title={knowWhyData?.name ?? ''}
          fundType={knowWhyData?.nature ?? ''}
          fundCategory={knowWhyData?.category ?? ''}
          investedAmount={knowWhyData?.investedAmount?.amount ?? 0}
          currentGain={knowWhyData?.currentGain?.amount ?? 0}
          powerForm={powerForm}
          isRegular={knowWhyData?.isRegular ?? false}
        />
      </div>

      {knowWhyData?.isRegular && (
        <div className='bg-white px-5 pt-5 pt-4 pb-1 w-full'>
          <div className='w-full flex gap-3 items-center border border-[#F0F4F7] rounded-xl px-4 py-3'>
            <div className='rounded-[10px] bg-[#004D99] w-[3px] h-[20px]' aria-hidden='true'></div>
            <div className='text-[#004D99] text-[13px] font-[400]'>
              Showing performance for <span className='font-[500]'> Direct Growth Plan </span>
            </div>
          </div>
        </div>
      )}

      <div className='flex flex-col gap-3 bg-[#F0F4F7] w-full pb-16'>
        <div className='w-full flex flex-col'>
          <div className='w-full bg-white'>
            <div className='flex flex-col'>{scoreSections}</div>
          </div>
          <div className='w-full bg-white border-t border-[#F0F4F7]'>
            <div className='flex gap-4 items-center p-5 pb-7'>
              <div aria-hidden='true'>
                <RenderHTML innerHTMLText={FundManagersIcon} />
              </div>
              <div
                role={'text' as AriaRole}
                aria-label="Our ratings also account for the fund manager's track record and performance"
              >
                <p aria-hidden='true' className='text-[#232323] text-[13px] font-[400] leading-[140%] m-0'>
                  Our ratings also account for the fund <br /> manager&apos;s track record and performance
                </p>
              </div>
            </div>
          </div>
          {!knowWhyData?.isElite && (
            <div>
              <EliteBanner
                onBannerClick={() => {
                  trackEvent({
                    eventName: EventNames.ButtonTap,
                    payload: {
                      eventId: 'Upgrade to Elite_V1',
                      screenType: 'MF_Know_Why',
                      ScreenName
                    }
                  })
                }}
              />
            </div>
          )}
        </div>

        {recommendationsBanner && (
          <div className='w-full flex flex-col'>
            {recommendationsBanner}
            <ul role='list' aria-label='Recommendations' className='list-none m-0 p-0 flex flex-col'>
              {(recommendationPoints ?? []).map((point, index) => {
                const Icon = index === 0 ? CalendarIcon : RupeeIcon
                return (
                  <li key={index} role='listitem' className='list-none m-0 p-0'>
                    <div
                      role={'text' as AriaRole}
                      aria-label={stripHtml(point?.label ?? '')}
                      className='w-full bg-white border-t border-[#F0F4F7] text-[#6A6E6E] text-end p-5'
                    >
                      <div aria-hidden='true' className='flex items-center gap-4'>
                        <div>
                          <RenderHTML innerHTMLText={Icon} />
                        </div>
                        <RenderHTML
                          className='text-[#232323] font-[400] text-[14px] text-left w-10/12'
                          innerHTMLText={point?.label ?? ''}
                        />
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {nextStepsBanner && <div className='w-full '>{nextStepsBanner}</div>}

        {/* Separate paragraphs so each receives its own focus stop (See Why_B04); #6A6E6E for AA contrast (See Why_B07) */}
        <div className='pt-7 text-[#6A6E6E] text-[12px] font-[400] leading-[150%] w-full break-words px-5 flex flex-col gap-4'>
          <p className='m-0'>
            All recommendations are thoroughly backtested by our in-house SEBI registered experts.
          </p>
          <p className='m-0'>
            We understand that past success of backtesting doesn’t guarantee future returns, and neither do we. Rest
            assured, we are actively monitoring your portfolio with our proprietary rating engine built by our experts,
            to give the best possible results on your investments. We do the hard work, so you don’t have to.
          </p>
          <p className='m-0'>
            The recommendations are based on available data and uses our best judgement to help you keep your portfolio
            in top form. This is not a guarantee of returns in any way. The final judgement of acting on the
            recommendations lie solely with you.
          </p>
          <p className='m-0 text-[11px]'>
            Disclaimer: Information provided is intended solely for the client’s consumption and should not be
            redistributed. Mutual fund investments are subject to market risks. Registration granted by SEBI,
            enlistment as IA with Exchange and certification from NISM do not guarantee the performance of any
            intermediary or provide any assurance of returns.
          </p>
        </div>
      </div>
    </div>
  )
}

export const CalendarIcon = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<rect x="0.5" y="0.5" width="39" height="39" rx="19.5" stroke="#F0F4F7"/>
<path d="M14.8214 15.3391C14.5467 15.3391 14.2832 15.4482 14.089 15.6425C13.8948 15.8367 13.7856 16.1001 13.7856 16.3748V25.1784C13.7856 25.4531 13.8948 25.7166 14.089 25.9108C14.2832 26.1049 14.5467 26.2141 14.8214 26.2141H25.1785C25.4532 26.2141 25.7167 26.1049 25.9109 25.9108C26.1051 25.7166 26.2142 25.4531 26.2142 25.1784V16.3748C26.2142 16.1001 26.1051 15.8367 25.9109 15.6425C25.7167 15.4482 25.4532 15.3391 25.1785 15.3391H14.8214Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.7856 18.9641H26.2142" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M16.375 13.7856V16.8928" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M23.625 13.7856V16.8928" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`

export const RupeeIcon = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<rect x="0.5" y="0.5" width="39" height="39" rx="19.5" stroke="#F0F4F7"/>
<g clip-path="url(#clip0_39856_17631)">
<path d="M20 26.7321C23.7181 26.7321 26.7321 23.7181 26.7321 20C26.7321 16.2819 23.7181 13.2678 20 13.2678C16.2819 13.2678 13.2678 16.2819 13.2678 20C13.2678 23.7181 16.2819 26.7321 20 26.7321Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M17.2812 17.1519H22.7188" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M17.2812 19.2878H22.7188" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M19.6114 17.1519C20.1293 17.2813 21.165 17.7344 21.165 19.0938C21.165 21.4242 18.0579 21.4242 18.0579 21.4242C18.0579 21.4242 19.6114 22.7836 21.9418 23.3661" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</g>
<defs>
<clipPath id="clip0_39856_17631">
<rect width="16" height="16" fill="white" transform="translate(12 12)"/>
</clipPath>
</defs>
</svg>
`

export const FundManagersIcon = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M20 0.75C30.6315 0.75 39.25 9.36852 39.25 20C39.25 30.6315 30.6315 39.25 20 39.25C9.36852 39.25 0.75 30.6315 0.75 20C0.75 9.36852 9.36852 0.75 20 0.75Z" stroke="#F0F4F7" stroke-width="1.5"/>
<g clip-path="url(#clip0_52148_35050)">
<path d="M16.2044 12.3328C15.1059 12.6734 14.3081 13.6975 14.3081 14.9079C14.3081 16.1183 15.1059 17.1424 16.2044 17.483" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M20.6004 17.6036C19.1117 17.6036 17.9048 16.3968 17.9048 14.908C17.9048 13.4193 19.1117 12.2124 20.6004 12.2124C22.0891 12.2124 23.296 13.4193 23.296 14.908C23.296 16.3968 22.0891 17.6036 20.6004 17.6036Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M24.3393 21.1977C23.461 20.1021 22.1115 19.4006 20.5981 19.4006C17.9515 19.4006 15.8059 21.5462 15.8059 24.1928H18.801" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.4109 24.1929H12.2129C12.2129 22.3377 13.2671 20.7288 14.8091 19.9324" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M27.786 21.7969L22.9938 27.7871L20.5977 25.9901" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</g>
<defs>
<clipPath id="clip0_52148_35050">
<rect width="18" height="18" fill="white" transform="translate(11 11)"/>
</clipPath>
</defs>
</svg>
`
